import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Activity,
  ArrowUp,
  Package,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ShieldCheck,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import complianceService from '../services/complianceService';

export default function DashboardPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [complianceDashboardData, setComplianceDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const [general, compliance] = await Promise.allSettled([
        complianceService.getDashboard(),
        complianceService.getComplianceDashboard(),
      ]);
      if (general.status === 'fulfilled') setDashboardData(general.value);
      if (compliance.status === 'fulfilled') setComplianceDashboardData(compliance.value);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Derive stats from API or fall back to zero/empty
  const stats = {
    totalOperators: dashboardData?.totalOperators ?? complianceDashboardData?.totalOperators ?? 0,
    activeLicenses: complianceDashboardData?.activeLicenses ?? dashboardData?.activeLicenses ?? 0,
    inventoryValue: dashboardData?.inventoryValue ?? 0,
    complianceScore: complianceDashboardData?.complianceScore ?? dashboardData?.complianceScore ?? 0,
    pendingReports: complianceDashboardData?.pendingReports ?? dashboardData?.pendingReports ?? 0,
    expiringLicenses: complianceDashboardData?.expiringLicenses ?? dashboardData?.expiringLicenses ?? 0,
    pendingCOAs: complianceDashboardData?.pendingCOAs ?? dashboardData?.pendingCOAs ?? 0,
    inventoryUnits: complianceDashboardData?.inventoryUnits ?? dashboardData?.inventoryUnits ?? 0,
  };

  const complianceHealthData = complianceDashboardData?.complianceHealth ?? dashboardData?.complianceHealth ?? [];
  const revenueData = dashboardData?.revenueData ?? complianceDashboardData?.revenueData ?? [];
  const licenseExpirationData = complianceDashboardData?.licenseExpirationData ?? dashboardData?.licenseExpirationData ?? [];
  const recentAlerts = complianceDashboardData?.recentAlerts ?? dashboardData?.recentAlerts ?? [];
  const recentActivity = dashboardData?.recentActivity ?? complianceDashboardData?.recentActivity ?? [];

  const statCards = [
    {
      title: 'Total Operators',
      value: stats.totalOperators.toLocaleString(),
      icon: Users,
      description: 'Total registered operators',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Active Licenses',
      value: stats.activeLicenses.toLocaleString(),
      icon: ShieldCheck,
      description: 'Currently active licenses',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Inventory Value',
      value: stats.inventoryValue > 0 ? `$${(stats.inventoryValue / 1000000).toFixed(2)}M` : '$0',
      icon: Package,
      description: 'Total inventory value',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Compliance Score',
      value: `${stats.complianceScore}%`,
      icon: CheckCircle,
      description: 'Overall compliance rating',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
  ];

  const quickStats = [
    { label: 'Pending Reports', value: stats.pendingReports, icon: FileText, color: 'text-orange-600', bgColor: 'bg-orange-50' },
    { label: 'Expiring Licenses', value: stats.expiringLicenses, icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-50' },
    { label: 'Pending COAs', value: stats.pendingCOAs, icon: Clock, color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
    { label: 'Inventory Units', value: stats.inventoryUnits.toLocaleString(), icon: Package, color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard Overview</h1>
            <p className="text-muted-foreground mt-1">Monitor your cannabis operations and compliance status</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchDashboardData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardDescription className="text-sm font-medium">{stat.title}</CardDescription>
                <div className={`p-2 ${stat.bgColor} rounded-lg`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Stats Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickStats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div className={`p-3 ${stat.bgColor} rounded-lg`}>
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Compliance Health & Revenue Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Compliance Health */}
          <Card>
            <CardHeader>
              <CardTitle>Compliance Health</CardTitle>
              <CardDescription>Overall operator compliance status</CardDescription>
            </CardHeader>
            <CardContent>
              {complianceHealthData.length > 0 && complianceHealthData.some(d => d.value > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={complianceHealthData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {complianceHealthData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {complianceHealthData.map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                          <span>{item.name}</span>
                        </div>
                        <span className="font-medium">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <CheckCircle className="h-10 w-10 mb-2 opacity-30" />
                  <p className="text-sm">No compliance data yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue & Inventory Trend */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Revenue & Inventory Trend</CardTitle>
              <CardDescription>Financial performance overview</CardDescription>
            </CardHeader>
            <CardContent>
              {revenueData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorInventory" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#colorRevenue)" />
                    <Area type="monotone" dataKey="inventory" stroke="#6366f1" fillOpacity={1} fill="url(#colorInventory)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <TrendingUp className="h-10 w-10 mb-2 opacity-30" />
                  <p className="text-sm">No trend data available yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* License Expiration & Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* License Expiration */}
          <Card>
            <CardHeader>
              <CardTitle>License Expiration Timeline</CardTitle>
              <CardDescription>Licenses by expiration period</CardDescription>
            </CardHeader>
            <CardContent>
              {licenseExpirationData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={licenseExpirationData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                      {licenseExpirationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || '#6366f1'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <ShieldCheck className="h-10 w-10 mb-2 opacity-30" />
                  <p className="text-sm">No license data available yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Alerts */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Alerts</CardTitle>
              <CardDescription>System notifications and warnings</CardDescription>
            </CardHeader>
            <CardContent>
              {recentAlerts.length > 0 ? (
                <div className="space-y-4">
                  {recentAlerts.slice(0, 5).map((alert, index) => (
                    <div key={alert.id ?? index} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                      <div className="mt-0.5">
                        {alert.type === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
                        {alert.type === 'warning' && <AlertTriangle className="h-5 w-5 text-orange-600" />}
                        {alert.type === 'success' && <CheckCircle className="h-5 w-5 text-green-600" />}
                        {!alert.type && <Activity className="h-5 w-5 text-blue-600" />}
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">{alert.title ?? alert.message}</p>
                        {alert.description && <p className="text-xs text-muted-foreground">{alert.description}</p>}
                        {alert.time && <p className="text-xs text-muted-foreground">{alert.time}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <CheckCircle className="h-10 w-10 mb-2 opacity-30" />
                  <p className="text-sm">No recent alerts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        {recentActivity.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest system events</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary"></div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium">{activity.action ?? activity.description}</p>
                      {activity.entity && <p className="text-xs text-muted-foreground">{activity.entity}</p>}
                      {activity.time && <p className="text-xs text-muted-foreground">{activity.time}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
